import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  FileText, Plus, Edit, Trash2, CheckCircle, Clock, XCircle, Eye, Search, Filter,
  AlertCircle, Mail, Send, ShieldCheck, RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft:     { label: 'Draft',     icon: Edit,        cls: 'bg-gray-100 text-gray-700' },
  pending:   { label: 'Pending',   icon: Clock,       cls: 'bg-yellow-100 text-yellow-800' },
  approved:  { label: 'Approved',  icon: CheckCircle, cls: 'bg-green-100 text-green-800' },
  rejected:  { label: 'Rejected',  icon: XCircle,     cls: 'bg-red-100 text-red-800' },
};

const CONTEXT_LABELS = {
  general: 'General',
  investor_account: 'Investor Account',
  drawdown_investor: 'Drawdown',
};

const EmailTemplatesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [scope, setScope] = useState('mine'); // mine | approved | pending | all
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectModal, setRejectModal] = useState(null); // { id, name }
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { fetchTemplates(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => { if (isAdmin) fetchPendingCount(); /* eslint-disable-next-line */ }, [isAdmin]);

  const fetchTemplates = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/email-templates?scope=${scope}`);
      setTemplates(data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load templates');
    } finally { setLoading(false); }
  };

  const fetchPendingCount = async () => {
    try {
      const { data } = await axios.get(`${API}/email-templates?scope=pending`);
      setPendingCount((data || []).length);
    } catch { /* ignore */ }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.subject || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  const tabs = [
    { id: 'mine', label: 'My Templates' },
    { id: 'approved', label: 'Approved (AMC)' },
    ...(isAdmin ? [
      { id: 'pending', label: `Pending Approval${pendingCount > 0 ? ` (${pendingCount})` : ''}`, badge: pendingCount },
      { id: 'all', label: 'All Templates' },
    ] : []),
  ];

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/email-templates/${tpl.id}`);
      fetchTemplates(); fetchPendingCount();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (tpl) => {
    if (!window.confirm(`Submit "${tpl.name}" for Admin approval?`)) return;
    try {
      await axios.post(`${API}/email-templates/${tpl.id}/submit`);
      fetchTemplates(); fetchPendingCount();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to submit'); }
  };
  const handleApprove = async (tpl) => {
    try {
      await axios.post(`${API}/email-templates/${tpl.id}/approve`);
      fetchTemplates(); fetchPendingCount();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to approve'); }
  };
  const handleRevoke = async (tpl) => {
    if (!window.confirm(`Revoke approval of "${tpl.name}"? It will return to Draft.`)) return;
    try {
      await axios.post(`${API}/email-templates/${tpl.id}/revoke`);
      fetchTemplates(); fetchPendingCount();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to revoke'); }
  };
  const submitReject = async () => {
    if (!rejectModal) return;
    try {
      await axios.post(`${API}/email-templates/${rejectModal.id}/reject`, { reason: rejectReason });
      setRejectModal(null); setRejectReason('');
      fetchTemplates(); fetchPendingCount();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reject'); }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center" data-testid="email-templates-title">
            <FileText className="mr-3" size={32} />
            Email Templates
          </h1>
          <p className="text-gray-600 mt-1">Reusable email templates with merge fields and admin approval.</p>
        </div>
        <Link
          to="/email-templates/new"
          className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-blue-700 flex items-center"
          data-testid="email-templates-create-btn"
        >
          <Plus size={18} className="mr-2" /> New Template
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start">
          <AlertCircle size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Scope tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex flex-wrap items-center gap-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setScope(t.id)}
              data-testid={`tpl-scope-${t.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                scope === t.id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
              {t.id === 'pending' && t.badge > 0 && (
                <span className="ml-2 inline-flex items-center justify-center text-[10px] bg-red-500 text-white rounded-full h-4 min-w-4 px-1">{t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, subject, or description..."
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading templates...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="email-templates-empty">
            <Mail className="mx-auto text-gray-300 mb-3" size={40} />
            <p className="text-gray-500">No templates {search ? 'match your search' : 'yet'}.</p>
            {scope === 'mine' && !search && (
              <Link to="/email-templates/new" className="inline-block mt-3 text-blue-600 hover:underline text-sm">
                Create your first template →
              </Link>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Context</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Subject</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Updated</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(tpl => {
                const meta = STATUS_META[tpl.status] || STATUS_META.draft;
                const StatusIcon = meta.icon;
                const isOwner = tpl.user_id === user?.id;
                return (
                  <tr key={tpl.id} className="hover:bg-gray-50" data-testid={`tpl-row-${tpl.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/email-templates/${tpl.id}`} className="text-blue-700 hover:underline font-medium">{tpl.name}</Link>
                      {tpl.description && <p className="text-xs text-gray-500 mt-0.5">{tpl.description}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{CONTEXT_LABELS[tpl.context_type] || tpl.context_type}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{tpl.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${meta.cls}`} data-testid={`tpl-status-${tpl.id}`}>
                        <StatusIcon size={12} className="mr-1" />
                        {meta.label}
                      </span>
                      {tpl.status === 'rejected' && tpl.rejection_reason && (
                        <p className="text-[11px] text-red-600 mt-1 italic">"{tpl.rejection_reason}"</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link to={`/email-templates/${tpl.id}`} className="text-gray-600 hover:text-blue-600 inline-flex items-center text-xs" title="View"><Eye size={14} /></Link>
                        {isOwner && (
                          <Link to={`/email-templates/${tpl.id}/edit`} className="text-gray-600 hover:text-blue-600 inline-flex items-center text-xs" title="Edit" data-testid={`tpl-edit-${tpl.id}`}><Edit size={14} /></Link>
                        )}
                        {isOwner && (tpl.status === 'draft' || tpl.status === 'rejected') && (
                          <button onClick={() => handleSubmit(tpl)} className="text-blue-600 hover:bg-blue-50 px-2 py-1 rounded text-xs flex items-center" data-testid={`tpl-submit-${tpl.id}`}>
                            <Send size={12} className="mr-1" /> Submit
                          </button>
                        )}
                        {isAdmin && tpl.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(tpl)} className="text-green-700 hover:bg-green-50 px-2 py-1 rounded text-xs flex items-center" data-testid={`tpl-approve-${tpl.id}`}>
                              <ShieldCheck size={12} className="mr-1" /> Approve
                            </button>
                            <button onClick={() => { setRejectModal({ id: tpl.id, name: tpl.name }); setRejectReason(''); }} className="text-red-700 hover:bg-red-50 px-2 py-1 rounded text-xs flex items-center" data-testid={`tpl-reject-${tpl.id}`}>
                              <XCircle size={12} className="mr-1" /> Reject
                            </button>
                          </>
                        )}
                        {isAdmin && tpl.status === 'approved' && (
                          <button onClick={() => handleRevoke(tpl)} className="text-yellow-700 hover:bg-yellow-50 px-2 py-1 rounded text-xs flex items-center" data-testid={`tpl-revoke-${tpl.id}`}>
                            <RotateCcw size={12} className="mr-1" /> Revoke
                          </button>
                        )}
                        {(isOwner || isAdmin) && (
                          <button onClick={() => handleDelete(tpl)} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Delete" data-testid={`tpl-delete-${tpl.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Reject reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject template?</h3>
            <p className="text-sm text-gray-600 mb-4">Provide a short reason so the owner can fix and resubmit “{rejectModal.name}”.</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={4}
              placeholder="e.g. Subject line is too generic — please mention the drawdown number."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
              data-testid="tpl-reject-reason"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejectModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={submitReject} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium" data-testid="tpl-reject-confirm">Reject Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesList;
